import React, { useEffect, useRef } from 'react';
import Quote from '../components/Quote';
import { Link } from 'react-router-dom';
import { SiteContent } from '../content';

const seedImages: Record<number, string> = {
  1: '/images/Ethereal Ink Watercolor Sketch.png',
  2: '/images/Warm Ink Sketch Journal.png',
  3: '/images/technicianofthesacred_Detailed_pen_sketch_of_scarab_beetle_pu_6ea13682-5018-4121-bd68-7ed07e8997bf_3.png',
  4: '/images/erb-cert.jpg',
  5: '/images/technicianofthesacred_Coins_tumbling_in_waterfall_formation_o_eb018396-21e0-4c14-bdb1-0a1cd805edd1_3.png',
  6: '/images/Simple Ink Drawing of Plant.png',
};

const Goals: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.01, rootMargin: "0px 0px 100px 0px" });

    const elements = containerRef.current?.querySelectorAll('.animate-on-scroll');
    elements?.forEach(el => {
      if (el.getBoundingClientRect().top >= window.innerHeight) {
        el.classList.add('below-fold');
        observer.observe(el);
      }
    });

    return () => observer.disconnect();
  }, []);

  const { hero, phases, seeds, closing } = SiteContent.goals;

  return (
    <div ref={containerRef} className="flex flex-col w-full bg-paper relative overflow-hidden">

      {/* SECTION 1: HERO */}
      <section className="pt-40 md:pt-64 pb-20 md:pb-40 text-center px-6 md:px-10 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="animate-on-scroll animate-fade-up">
            <h1 className="section-title text-5xl md:text-7xl lg:text-8xl mb-8 leading-[0.9]">{hero.title}</h1>
          </div>
          <div className="animate-on-scroll animate-fade-up delay-200">
            <p className="text-xl md:text-2xl max-w-3xl mx-auto text-[#1A1A1A]/60 font-medium leading-relaxed italic">{hero.subtitle}</p>
          </div>
        </div>
      </section>

      {/* SECTION 2: PHASES (NEW) */}
      <section className="pb-32 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 animate-on-scroll animate-fade-up">
            <span className="editorial-label">{phases.title}</span>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {phases.items.map((phase, i) => (
              <div key={i} className={`p-8 border border-black/10 rounded-xl bg-white/50 backdrop-blur-sm animate-on-scroll animate-fade-up ${phase.status === 'WE ARE HERE' ? 'ring-2 ring-[#A65D3F] bg-white shadow-xl' : 'opacity-60 grayscale hover:grayscale-0 hover:opacity-100 transition-all'}`} style={{ transitionDelay: `${i * 150}ms` }}>
                <div className="flex justify-between items-center mb-6">
                  <span className="text-xs font-black uppercase tracking-widest text-black/50">Phase 0{i + 1}</span>
                  <span className={`text-[11px] font-bold px-3 py-1.5 rounded-full ${phase.status === 'WE ARE HERE' ? 'bg-[#A65D3F] text-white' : 'bg-black/10 text-black/60'}`}>{phase.status}</span>
                </div>
                <h3 className="font-condensed text-2xl font-black mb-6">{phase.title.replace(/PHASE \d+: /, '')}</h3>
                <p className="text-sm font-medium leading-relaxed text-black/70 whitespace-pre-wrap">{phase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3: SEEDS (TIMELINE) */}
      <div className="max-w-7xl mx-auto px-4 md:px-10 relative pb-32 md:pb-60">
        <div className="text-center mb-20 animate-on-scroll animate-fade-up">
          <h2 className="section-title mb-8">{seeds.title}</h2>
          <p className="text-xl max-w-2xl mx-auto text-black/60 italic">{seeds.description}</p>
        </div>

        <div className="absolute top-40 bottom-0 left-8 md:left-1/2 md:-translate-x-px w-px bg-[#A65D3F]/20 z-0"></div>

        {seeds.items.map((seed, idx) => {
          const isEven = idx % 2 === 0;
          return (
            <div key={seed.id} className={`relative grid md:grid-cols-2 gap-12 md:gap-24 items-center mb-32 md:mb-56 group ${!isEven ? 'md:text-right' : 'md:text-left'}`}>
              <div className="absolute left-8 md:left-1/2 -translate-x-1/2 w-4 h-4 bg-[#F5F0E6] border-2 border-[#A65D3F] rounded-full z-10 transition-transform duration-500 group-hover:scale-150"></div>

              {/* Number/Icon Side */}
              <div className={`relative pl-12 md:pl-0 ${!isEven ? 'md:order-2' : 'md:order-1'}`}>
                <div className="animate-on-scroll animate-scale-in">
                  <div className={`relative aspect-square md:aspect-[4/3] bg-white border border-black/5 overflow-hidden shadow-xl rounded-2xl flex items-center justify-center md:group-hover:bg-[#3D2B1F] transition-colors duration-700 cursor-default`}>
                    {seedImages[seed.id] && (
                      <img
                        src={seedImages[seed.id]}
                        alt={seed.title}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-20 transition-opacity duration-700"
                      />
                    )}
                    {/* Desktop: hover to reveal */}
                    <div className="absolute inset-0 hidden md:flex flex-col items-center justify-center p-8 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      <Quote text={seed.quote} color="cream" size="sm" />
                    </div>
                    {/* Mobile: always visible quote below number */}
                    <div className="absolute inset-0 md:hidden flex flex-col items-center justify-end p-6 bg-gradient-to-t from-[#3D2B1F]/90 to-transparent">
                      <Quote text={seed.quote} color="cream" size="sm" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Content Side */}
              <div className={`relative pl-12 md:pl-0 ${!isEven ? 'md:order-1 md:pr-12' : 'md:order-2 md:pl-12'}`}>
                <div className="animate-on-scroll animate-fade-up delay-200">
                  <h2 className="section-title text-3xl md:text-5xl mb-6">{seed.title}</h2>
                </div>
                <div className="animate-on-scroll animate-fade-up delay-300">
                  <p className="text-lg md:text-xl font-medium text-black/70 mb-10 leading-relaxed max-w-lg inline-block">
                    {seed.description}
                  </p>
                </div>
                <div className="bg-white/60 p-6 md:p-8 rounded-xl border border-[#A65D3F]/20 shadow-lg animate-on-scroll animate-fade-up delay-400">
                  <ul className="space-y-4">
                    {seed.features.map((item, i) => (
                      <li key={i} className={`${!isEven ? 'md:text-right' : ''}`}>
                        <span className="text-xs font-bold tracking-widest uppercase">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* CLOSING (Dark Background) */}
      <section className="section-padding bg-rich-black text-center relative overflow-hidden">
        <img
          src="/images/home-hero.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-10 -scale-y-100"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent" />
        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <div className="animate-on-scroll animate-fade-up">
            <h2 className="section-title text-[#F5F0E6] mb-12">{closing.title}</h2>
            <p className="text-white/70 text-lg md:text-xl font-medium mb-12 max-w-2xl mx-auto">{closing.text}</p>
            <Quote large text={closing.quote} className="mb-16" />
          </div>
          <div className="animate-on-scroll animate-scale-in delay-200">
            <Link to="/shop" className="btn-primary px-16">{SiteContent.common.shopAlphabet}</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Goals;